import { IParams, Nullable } from '@app/types';
import { Checkbox } from '@hooks/envelopes/types';

import {
  TasksStatus,
  EnvelopeAllovedActions,
  EnvelopeDirection,
  EnvelopeFlowState,
  RecipientAccessAuthorizedBy,
  FlowStageState,
  DraftEnvelopePrepositionedMarkFont, DraftEnvelopePrepositionedMarkType, DraftEnvelopePrepositionedMarkFontType, EnvelopeFlowResult
} from '@const/consts'
import { IFileProperties } from '@app/utils/types';
import {
  EnvelopeTemplateAnchorOccurrenceMode,
  EnvelopeTemplatePageSearchMode,
  EnvelopeTemplatePlacementMode,
  EnvelopeTemplateXPlacement,
  EnvelopeTemplateYPlacement,
  IContactDirectoryResolveRecipientRule,
  IEnvelopeRouteNotifications,
  ILabel,
  IEnvelopeSelectResolvedValue,
  IEnvelopeTemplateFile,
  IEnvelopeTemplateStartCondition
} from '../boxes/types';

export interface IEnvelope {
  oguid: string
  sender: IEnvelopeSender
  direction?: Nullable<EnvelopeDirection>
  directions: Nullable<EnvelopeDirection[]>
  lastActionDateTime: string
  flowStartDateTime: string
  flowCompletedDateTime: string
  subject: string
  taskTotalCount: number
  taskCompletedCount: number
  currentUserTaskOguid: Nullable<string>
  allowedActions: EnvelopeAllovedActions[]
  allowedMassActions: EnvelopeAllovedActions[]
  fields: Nullable<IEnvelopeFields>
  flowState: EnvelopeFlowState
  recipients: IRecipients[]
  sharedLinkOguid: string
  /** Ящик, к которому относится конверт */
  box: IEnvelopeBox;
  /** Признак прочтения / не прочтения
    - для входящих конвертов соответствует состоянию назначенной на пользователя задачи (если задача прочитана, будет true)
    - для исходящих задач всегда возвращается true т.к. отправленный конверт считается автоматически прочтенным
  */
  isRead: boolean;
  /** Признак задачи, требующей действия текущего пользователя */
  isActionRequired: boolean;
  /** Ярлыки */
  labels: ILabel[];
  /** Контекст для поиска по query.like */
  content: string;
}

export interface IRecipients {
  email: Nullable<string>
  guestEmail: Nullable<string>
  fullName: Nullable<string>
  assignedToBoxUser: Nullable<IEnvelopeAssignedToBoxUser>
}

/** Ящик конверта */
export interface IEnvelopeBox {
  oguid: string;
  email: string;
}

export interface IEnvelopePost {
  senderBoxOguid: string
  fields: IEnvelopeFields
  subject: string
  comment: string
  externalId: string
  files: string[]
  expirationDateTime: string
  flow: IEnvelopePostFlow[]
}

export interface ICurrentEnvelope extends IEnvelope {
  comment: Nullable<string>
  commentFiles: Nullable<IEnvelopeCommentFile[]>
  externalId: Nullable<string>
  unreadTodosCount: number
  files: Nullable<IEnvelopeFiles[]>
  currentUserTaskOguid: Nullable<string>
  expirationDateTime: string
  flow: ICurrentEnvelopeFlow[]
  notifications: Nullable<IEnvelopeRouteNotifications>
  sharedLinkOguid: string
}

export interface IEnvelopeGenerateLink {
  fields: IEnvelopeFields
  subject: string
  files: IEnvelopeFiles[]
  recipient: IEnveloperPostRecipient
  redirectTo: Nullable<string>
}

export interface IEnvelopeStopAgreementProcess {
  dateTime: string
  correlationId: string
  error: string
  message: string
  debugMessage: string
}

export interface IEnvelopeDiscussionObjectApproval {
  oguid: string
  readDateTime: string
  author: IEnvelopeUser
  recipient: IEnvelopeUser
  comment: string
  description: string
  todos: Array<object> // TODO: [ { } ], Array of objects or null (todo) Child TODO
  startedDateTime: string
  completedDateTime: string
}

export interface IEnvelopeSender {
  boxOguid: string
  boxEmail: string
  user: IEnvelopeUser
}

export interface IEnvelopeUser {
  oguid: string
  position: string
  email: string
  fullName: string
}

export interface IEnvelopeAssignedToBoxUser extends IEnvelopeUser {
  phone: Nullable<string | number>
}

export interface ICurrentEnvelopeFlow {
  oguid: Nullable<string>
  stageKey: Nullable<string>
  labels: ILabel[]
  description: Nullable<string>
  state: Nullable<FlowStageState>
  tasks: IEnvelopeTasks[]
}

export interface IEnvelopeTasks {
  oguid: string
  type: TasksStatus
  comment?: Nullable<string>
  readDateTime: Nullable<string>
  state: Nullable<FlowStageState>
  result: Nullable<EnvelopeFlowResult>
  recipient: ICurrentEnvelopeRecipient
  startedDateTime: string
  completedDateTime: Nullable<string>
  completedSigner: Nullable<IEnveloperCompletedSigner>
  completedEvidences: Nullable<IEnveloperCompletedEvidences>
}

export interface ICurrentEnvelopeRecipient {
  recipientKey: Nullable<string>
  boxOguid: Nullable<string>
  email: Nullable<string>
  assignedToBoxUser: Nullable<IEnvelopeAssignedToBoxUser>
  isAssignedToBoxUser: boolean
  isHostMode: Nullable<boolean>
  guestEmail: Nullable<string>
  declaredSigner: Nullable<IEnveloperSigner>
  isRequireSelfie: boolean
  isRequireSms: boolean
  isSignOnBehalfOfOrganization?: Nullable<boolean>
  prepositionedMarks?: Nullable<IEnveloperPrepositionedMark[]>
  signingParams: IDraftEnvelopeRecipientSigningParams
}

export interface IEnvelopePostFlow {
  recipients: IEnveloperPostRecipient[]
}

export interface IEnveloperPostRecipient {
  type: TasksStatus
  email?: Nullable<string>
  assignedToBoxUserOguid?: Nullable<string>
  isHostMode: Nullable<boolean>
  guestEmail: Nullable<string>
  signer: Nullable<IEnveloperPostSigner>
  signingParams: Nullable<IDraftEnvelopeRecipientSigningParams>
  isAllowElectronicSignature?: boolean
  isRequireSelfieToSignElectronically: boolean
  isRequireSmsToSignElectronically: boolean
  isSignOnBehalfOfOrganization?: Nullable<boolean>
  prepositionedMarks: IEnveloperPrepositionedMark[]
}

export interface IEnveloperSigner {
  fullName: string
  initials: Nullable<string>
  position: Nullable<string>
  phone: Nullable<number>
}

export interface IEnveloperPostSigner {
  fullName: Nullable<string>
  initials: Nullable<string>
  position: Nullable<string>
  phone: Nullable<number>
}

export interface IEnveloperCompletedSigner {
  oguid: string
  position: string
  email: string
  fullName: string
  initials: string
  phone: string
}

export interface IEnveloperCompletedEvidences {
  isVerifiedBySms: boolean
  phone: number
  isSelfieProvided: boolean
  selfieFileOguid: string
  geolocation: string
  userAgent: string
  recipientAccessAuthorizedBy: RecipientAccessAuthorizedBy
}

export interface IEnveloperPrepositionedMark {
  fileIndex: number
  key: string
  pageNumber: number
  topLeftX: number
  topLeftY: number
  height: number
  width: number
  type: string
  isReadOnly: boolean
  isRequired: boolean
  valueList: Nullable<string[]>
  value: Nullable<string>
  fontSize: number
  fontType: string
  font: string
  color: string
  scale: number
  tooltip: string
  label?: Nullable<string>
}

export interface IEnvelopeFiles {
  oguid?: string
  fileOguid?: string
  originalFileOguid?: string
  index: number
  filename: string
  originalProperties: IFileProperties
  signedProperties: Nullable<IFileProperties>
  pages: Nullable<string>
}

export interface IEnvelopeCommentFile {
  oguid: string
  filename: string
  size: number
  mimeType: string
}

export type IEnvelopeFieldScalarValue = boolean | number | string | null
export type IEnvelopeFieldTableValue = Array<Record<string, IEnvelopeFieldScalarValue>>
export type IEnvelopeFieldGetValue = IEnvelopeFieldScalarValue | IEnvelopeFieldTableValue | IEnvelopeSelectResolvedValue

export interface IEnvelopeFields {
  [key: string]: IEnvelopeFieldGetValue
}

export interface IEnvelopesListParams {
  direction: Nullable<EnvelopeDirection | undefined>
  boxOguid?: string
  page?: Nullable<string>
  perPage?: Nullable<string>
  // остальные параметры поиска
  otherSearchParams?: IParams
}

export interface IEnvelopeParams {
  oguid: string
  boxOguid: string
  direction?: Nullable<EnvelopeDirection>
}

export interface IGetPreviewParams {
  fileUuid: string
  requestUuid?: string
}

export interface IUploadFileParams {
  data: FormData
  fileIndex?: number
}

export interface IPreviewDimensions {
  width: number,
  height: number
}

export interface IPreviewData {
  dimensions: IPreviewDimensions
  fileCode: string
  mimeType: string
  orientation: string
}

export interface IGetPreview extends IPreviewData {
  totalPages: number
}

export interface IEnvelopeFilePreview {
  data: IPreviewData[]
  fileName: string
  isAvailable: boolean
  total: number
}

export interface IDraftEnvelopeRecipientSigner {
  fullName: string
  initials: Nullable<string>
  position: Nullable<string>
  phone: Nullable<string>
}

export enum DraftEnvelopeRecipientType {
  ACQUAINTANCE = 'ACQUAINTANCE',
  APPROVAL = 'APPROVAL',
  SIGNING = 'SIGNING'
}

export enum DraftEnvelopeRecipientSignerType {
  PART = 'PART',
  WITNESS = 'WITNESS'
}

export enum DraftEnvelopeRecipientSource {
  BOX_USER = 'BOX_USER',
  CONTACT_DIRECTORY = 'CONTACT_DIRECTORY',
  MANUAL = 'MANUAL'
}

export interface IDraftEnvelopeRecipientSigningParams {
  isAllowElectronicSignature: boolean
  isSignOnBehalfOfOrganization?: Nullable<boolean>
  signerType: DraftEnvelopeRecipientSignerType
}

export interface IDraftEnvelopeRecipientData {
  assignmentDescription?: Nullable<string>
  recipientKey?: Nullable<string>
  boxEmail: string
  boxPhone: Nullable<string>
  boxId: string
  assignedToBoxUserOguid?: Nullable<string>
  guestEmail: Nullable<string>
  isAssignedToBoxUser?: boolean
  isHostMode: boolean
  isNoPredefinedUser?: boolean
  isRequireSelfie: boolean
  isRequireSms: boolean
  isSignOnBehalfOfOrganization?: Nullable<boolean>
  prepositionedMarks: Nullable<IDraftEnvelopePrepositionedMarkData[]>
  resolveRecipientRule?: Nullable<IContactDirectoryResolveRecipientRule>
  signer: IDraftEnvelopeRecipientSigner
  signingParams: IDraftEnvelopeRecipientSigningParams
  startConditions?: Nullable<IEnvelopeTemplateStartCondition[]>
  type: DraftEnvelopeRecipientType
}

export interface IDraftEnvelopeRecipientDataRequest extends Omit<IDraftEnvelopeRecipientData, 'boxEmail' | 'boxPhone' | 'boxId' | 'isHostMode' | 'signer' | 'signingParams'> {
  boxId: Nullable<string>
  isHostMode: Nullable<boolean>
  signer: Nullable<IEnveloperPostSigner>
  signingParams: Nullable<IDraftEnvelopeRecipientSigningParams>
}

export interface IDraftEnvelopeRecipientCustomParam {
  id: string
  label: string
  value: Nullable<string>
}

export interface IDraftEnvelopeRecipient extends IDraftEnvelopeRecipientData {
  color: string,
  customParams: IDraftEnvelopeRecipientCustomParam[]
  groupNum: number
  recipientObjectGroupId?: string
  recipientSource?: DraftEnvelopeRecipientSource
  stageKey?: Nullable<string>
  stageLabels?: string[]
  stageDescription?: Nullable<string>
}

export interface IDraftEnvelopePrepositionedMarkData {
  anchorOccurrenceMode?: Nullable<EnvelopeTemplateAnchorOccurrenceMode>
  anchorOccurrenceNumber?: Nullable<number>
  anchorText?: Nullable<string>
  color: Nullable<string>
  fileIndex: number
  font: Nullable<DraftEnvelopePrepositionedMarkFont>
  fontSize: Nullable<number>
  fontType: Nullable<DraftEnvelopePrepositionedMarkFontType>
  height: Nullable<number>
  isLocked?: Nullable<boolean>
  isReadOnly: Nullable<boolean>
  isRequired: Nullable<boolean>
  key?: Nullable<string>
  pageNumber: number
  pageSearchFromPage?: Nullable<number>
  pageSearchMode?: Nullable<EnvelopeTemplatePageSearchMode>
  pageSearchPages?: Nullable<string>
  pageSearchToPage?: Nullable<number>
  placementMode?: Nullable<EnvelopeTemplatePlacementMode>
  scale: Nullable<number>
  tooltip: Nullable<string>
  topLeftX: number
  topLeftY: number
  type: DraftEnvelopePrepositionedMarkType,
  value: Nullable<string>
  valueList: Nullable<string[]>
  width: Nullable<number>
  xOffset?: Nullable<number>
  xPlacement?: Nullable<EnvelopeTemplateXPlacement>
  yOffset?: Nullable<number>
  yPlacement?: Nullable<EnvelopeTemplateYPlacement>
  /** лейбл к полю типа CUSTOM (обязательный для этого типа) */
  label?: Nullable<string>
  /** служебная связь с параметром получателя, в API не отправляется */
  customParamId?: Nullable<string>
}

export interface IDraftEnvelopePrepositionedMark extends IDraftEnvelopePrepositionedMarkData {
  recipient: string
}

export interface ISetDraftEnvelopePrepositionedMark {
  mark: Partial<IDraftEnvelopePrepositionedMark>
  uuid: string
}

export interface IDraftEnvelopeMessage {
  comment: Nullable<string>
  expirationDateTime: Nullable<Date>
  externalId: Nullable<string>
  subject: string
}

export interface IDraftEnvelopeCommentFile {
  oguid: string
  name: string
  size: number
  mimeType: string
}

export interface IDraftEnvelopeField {
  key: string,
  value: unknown
}

export interface IDraftEnvelope {
  commentFiles: IDraftEnvelopeCommentFile[]
  envelopeTemplate: Nullable<{
    isTemplateLocked?: boolean
    oguid: string;
  }>
  fields: Record<string, IDraftEnvelopeField>
  fieldsIdList: string[]
  files: string[]
  generatedTemplateFiles: Nullable<string[]>
  generatedTemplateFilesSubject: Nullable<string>
  labels: string[]
  notifications: Nullable<IEnvelopeRouteNotifications>
  message: IDraftEnvelopeMessage
  preview: {
    [uuid: string]: IEnvelopeFilePreview
  }
  recipients: Record<string, IDraftEnvelopeRecipient>
  recipientsColors: string[]
  recipientsIdList: string[]
  prepositionedMarks: Record<string, IDraftEnvelopePrepositionedMark>
  prepositionedMarksIdList: string[]
  templateFiles: Nullable<IEnvelopeTemplateFile[]>
  variablesIdList: string[]
}

export interface IGetPreviewThunkArg {
  fileOguid: string,
  page?: number
}

export interface IDraftEnvelopeFlowItem {
  stageKey: Nullable<string>
  labels: string[]
  description: Nullable<string>
  recipients: Array<IDraftEnvelopeRecipientDataRequest>
}

export interface IDraftEnvelopeData {
  comment: Nullable<string>
  commentFiles?: string[]
  expirationDateTime: Nullable<string>
  externalId: Nullable<string>
  fields: Nullable<Record<string, unknown>>
  variables?: Nullable<Record<string, unknown>>
  envelopeTemplate?: Nullable<{
    oguid: string;
  }>
  files: string[]
  flow: Array<IDraftEnvelopeFlowItem>
  labels: string[]
  notifications: Nullable<IEnvelopeRouteNotifications>
  senderBoxOguid: Nullable<string>
  subject: string
}

export interface IEnvelopesState {
  envelopes: {
    data: IEnvelope[],
    direction: Nullable<EnvelopeDirection | undefined>
  },
  envelopesListCache: Record<string, IEnvelope>
  checkedEnvelopes: Checkbox,
  isAllEnvelopesSelected: boolean,
  currentEnvelope: Nullable<ICurrentEnvelope>
  draftEnvelope: IDraftEnvelope
  // oguid текущего ящика
  currentBoxOguid: string
  currentUserFilterOguid: Nullable<string>
}

/** Данные для получения хеша файла */
export interface IEnvelopeDoHashData {
  /** Сертификат в base64 */
  base64Certificate: string;
  /** OGUID бокса */
  boxOguid: string,
  /** OGUID МЧД */
  mchdOguid?: Nullable<string>
}

/** Данные для подписи файлов */
export interface IEnvelopeDoSignItem {
  /** Хэш подписываемого файла, нужен для его поиска */
  hash: string;
  /** Cформированная Cades-Bes подпись в hex и base64 форматах */
  signatureBase64: string;
}

/** Данные для doSign */
export interface IEnvelopeDoSignData {
  signatures: IEnvelopeDoSignItem[]
}
